import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import CONSTANTS from 'shared/constants';
import { io } from 'socket.io-client';
import { UserStateContext } from './user-state-context';
const SOCKET_URLS = {
  PROD: 'https://agents-backend.prod.aws.cinch.io',
  DEV: 'https://agents-backend.dev.aws.cinch.io',
  LOCAL: `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:8020`
};
const SOCKET_URL = SOCKET_URLS[CONSTANTS.ENV] || SOCKET_URLS.LOCAL;
export const SocketContext = createContext();
export const SocketContextProvider = ({
  children
}) => {
  const {
    user,
    asCompany,
    token,
    flags
  } = useContext(UserStateContext);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const socketRef = useRef(null);
  const eventHandlersRef = useRef({});
  const showConsoleMessagesRef = useRef(false);
  const [enabled, setEnabled] = useState(true);
  const [waitingForReply, setWaitingForReply] = useState(false);
  const waitingForReplyRef = useRef(waitingForReply);

  // check if mobile browser
  const isMobileUserAgent = () => {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Keep the ref in sync with state
  useEffect(() => {
    waitingForReplyRef.current = waitingForReply;
  }, [waitingForReply]);
  useEffect(() => {
    try {
      // disables the AI Box feature on mobile devices because of sentry issues
      if (!isMobileUserAgent()) {
        localStorage.getItem('aiBoxEnabled') === 'true' ? setEnabled(true) : setEnabled(false);
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
  }, []);

  // Toggle detailed console messages for debugging
  const toggleConsoleMessages = () => {
    showConsoleMessagesRef.current = !showConsoleMessagesRef.current;
    console.log('Socket: Console messages', showConsoleMessagesRef.current ? 'enabled' : 'disabled');
  };
  const toggleEnabled = () => {
    if (enabled) {
      setEnabled(false);
      if (socketRef?.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnectionStatus('disconnected');
      localStorage.setItem('aiBoxEnabled', 'false');
    } else {
      setEnabled(true);
      localStorage.setItem('aiBoxEnabled', 'true');
    }
  };

  // Connection management
  useEffect(() => {
    if (!flags?.ai_chat_bot || !enabled) {
      return;
    }

    // Only connect if user is authenticated and we have required context
    if (!user?.id || !token || !asCompany?.id) {
      if (socketRef.current) {
        if (showConsoleMessagesRef?.current) {
          console.log('Socket: Disconnecting due to missing auth context');
        }
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnectionStatus('disconnected');
      }
      return;
    }

    // Don't reconnect if already connected with same user/company
    if (socketRef.current && connectionStatus === 'connected') {
      return;
    }
    if (showConsoleMessagesRef?.current) {
      console.log('Socket: Establishing connection to', SOCKET_URL);
    }
    setConnectionStatus('connecting');

    // Create socket connection - start with HTTP polling, then upgrade to WebSocket
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      // Start with polling, then upgrade
      upgrade: true,
      // Allow transport upgrades
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // 20 seconds for initial connection
      auth: {
        tokenProvider: 'cinch',
        token: token,
        userId: user.id
      }
    });

    // Connection event handlers
    socket.on('connect', () => {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Connected successfully via', socket.io.engine.transport.name);
      }
      setConnectionStatus('connected');
    });
    socket.on('disconnect', reason => {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Disconnected -', reason);
      }
      setConnectionStatus('disconnected');
    });
    socket.on('connect_error', error => {
      if (showConsoleMessagesRef?.current) {
        console.error('Socket: Connection error -', error);
      }
      setConnectionStatus('error');
    });
    socket.on('reconnect', attemptNumber => {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Reconnected after', attemptNumber, 'attempts');
      }
      setConnectionStatus('connected');
    });
    socket.on('reconnect_attempt', attemptNumber => {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Reconnection attempt', attemptNumber);
      }
      setConnectionStatus('connecting');
    });
    socket.on('reconnect_error', error => {
      if (showConsoleMessagesRef?.current) {
        console.error('Socket: Reconnection error -', error);
      }
      setConnectionStatus('error');
    });
    socket.on('reconnect_failed', () => {
      if (showConsoleMessagesRef?.current) {
        console.error('Socket: Reconnection failed');
      }
      setConnectionStatus('error');
    });

    // Transport upgrade events
    socket.io.on('upgrade', () => {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Transport upgraded to', socket.io.engine.transport.name);
      }
    });
    socket.io.on('upgradeError', error => {
      if (showConsoleMessagesRef?.current) {
        console.warn('Socket: Transport upgrade failed, staying on', socket.io.engine.transport.name, error);
      }
    });

    // Specific journey action event handlers with acknowledgement support
    const journeyActionEvents = ['ADD_JOURNEY_NODE', 'UPDATE_JOURNEY_NODE', 'DELETE_JOURNEY_NODE', 'ADD_JOURNEY_LINK', 'UPDATE_JOURNEY_LINK', 'DELETE_JOURNEY_LINK'];
    journeyActionEvents.forEach(eventName => {
      socket.on(eventName, (data, callback) => {
        if (showConsoleMessagesRef?.current) {
          console.log(`Socket: Received ${eventName} with acknowledgement`, data);
        }

        // Call registered handlers for this event
        const handlers = eventHandlersRef.current[eventName];
        if (handlers) {
          handlers.forEach(handler => {
            try {
              // Pass data with event name, and callback to support acknowledgement pattern
              handler(data, callback);
            } catch (error) {
              if (showConsoleMessagesRef?.current) {
                console.error(`Socket: Error in handler for ${eventName}`, error);
              }
            }
          });
        } else if (showConsoleMessagesRef?.current) {
          console.log(`[Socket]: No handlers registered for ${eventName}`);
        }
      });
    });

    // Generic message handler - capture all arguments including callbacks
    socket.onAny((eventName, ...args) => {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Received event', eventName, args);

        // DEBUG: Track all event types we're receiving
        if (eventName === 'AGENT_MESSAGE') {
          console.log('[Socket]: AGENT_MESSAGE event received!', args);
        } else if (eventName === 'AGENT_MESSAGE_TOKEN') {
          console.log('[Socket]:  AGENT_MESSAGE_TOKEN event received!', args);
        } else if (eventName.toLowerCase().includes('agent')) {
          console.log('[Socket]:  Agent-related event received:', eventName, args);
        } else if (eventName.toLowerCase().includes('message')) {
          console.log('[Socket]:  Message-related event received:', eventName, args);
        }
      }
      if (eventName === 'AGENT_MESSAGE' && waitingForReplyRef.current) {
        setWaitingForReply(false);
        if (showConsoleMessagesRef?.current) {
          console.log('Socket: Received AGENT_MESSAGE reply, waitingForReply set to false');
        }
      }

      // Call registered event handlers - pass all arguments for acknowledgement support
      const handlers = eventHandlersRef.current[eventName];
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(...args);
          } catch (error) {
            if (showConsoleMessagesRef?.current) {
              console.error('Socket: Error in event handler for', eventName, error);
            }
          }
        });
      } else {
        // DEBUG: Log when we receive events we're not subscribed to
        if (eventName !== 'connect' && eventName !== 'disconnect' && eventName !== 'connect_error' && showConsoleMessagesRef.current) {
          console.log('[Socket]: No handlers registered for event:', eventName);
        }
      }
    });
    socketRef.current = socket;

    // Cleanup function
    return () => {
      if (socket) {
        if (showConsoleMessagesRef?.current) {
          console.log('Socket: Cleaning up connection');
        }
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, [user?.id, token, asCompany?.id, flags?.ai_chat_bot, enabled]);

  // Send message function
  const sendMessage = useCallback((event, data) => {
    if (socketRef.current && connectionStatus === 'connected') {
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: Sending message', event, data);
      }
      socketRef.current.emit(event, data);
      setWaitingForReply(true);
      if (showConsoleMessagesRef?.current) {
        console.log('Socket: USER_MESSAGE sent, waitingForReply set to true');
      }
      return true;
    } else {
      if (showConsoleMessagesRef?.current) {
        console.warn('Socket: Cannot send message - not connected');
      }
      return false;
    }
  }, [connectionStatus]);

  // Subscribe to events
  const subscribe = useCallback((eventName, handler) => {
    if (!eventHandlersRef.current[eventName]) {
      eventHandlersRef.current[eventName] = [];
    }
    eventHandlersRef.current[eventName].push(handler);
    if (showConsoleMessagesRef?.current) {
      console.log('Socket: Subscribed to event', eventName);
    }

    // Return unsubscribe function
    return () => {
      const handlers = eventHandlersRef.current[eventName];
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
          if (showConsoleMessagesRef?.current) {
            console.log('Socket: Unsubscribed from event', eventName);
          }
        }
      }
    };
  }, []);

  // Unsubscribe from events
  const unsubscribe = useCallback((eventName, handler) => {
    const handlers = eventHandlersRef.current[eventName];
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
        if (showConsoleMessagesRef?.current) {
          console.log('Socket: Unsubscribed from event', eventName);
        }
      }
    }
  }, []);

  // Get connection info
  const getConnectionInfo = useCallback(() => {
    return {
      status: connectionStatus,
      connected: connectionStatus === 'connected',
      socketId: socketRef.current?.id || null,
      url: SOCKET_URL
    };
  }, [connectionStatus]);
  const contextValue = {
    socket: socketRef.current,
    connectionStatus,
    sendMessage,
    subscribe,
    unsubscribe,
    getConnectionInfo,
    showConsoleMessagesRef,
    toggleConsoleMessages,
    enabled,
    toggleEnabled,
    waitingForReply
  };
  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};